import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Globe,
    Search,
    MapPin,
    CheckCircle2,
    X
} from 'lucide-react';
import { JURISDICTION_GROUPS } from '../../../utils/jurisdictionData';

const JurisdictionSelector = ({
    onSelectCity,
    onSelectCounty,
    activeState,
    className = ""
}) => {
    const [searchTerm, setSearchTerm] = useState("");

    // Filter groups by active state if provided
    const visibleGroups = useMemo(() => 
        activeState ? JURISDICTION_GROUPS.filter(g => g.state === activeState) : JURISDICTION_GROUPS,
    [activeState]);

    const [activeCounty, setActiveCounty] = useState(visibleGroups[0]?.county || JURISDICTION_GROUPS[0].county);

    // Reset active county when state changes
    React.useEffect(() => {
        if (visibleGroups.length > 0 && !visibleGroups.find(g => g.county === activeCounty)) {
            setActiveCounty(visibleGroups[0].county);
        }
    }, [activeState, visibleGroups, activeCounty]);

    // Aggregate total cities (within active state filter)
    const totalCities = useMemo(() => 
        visibleGroups.reduce((acc, curr) => acc + curr.cities.length, 0), 
    [visibleGroups]);

    const filteredCities = useMemo(() => {
        if (!searchTerm) return [];
        return visibleGroups.flatMap(group => 
            group.cities
                .filter(city => city.toLowerCase().includes(searchTerm.toLowerCase()))
                .map(city => ({ city, county: group.county, color: group.color }))
        );
    }, [searchTerm, visibleGroups]);

    const activeGroup = useMemo(() => 
        visibleGroups.find(g => g.county === activeCounty),
    [activeCounty, visibleGroups]);

    return (
        <div className={`w-full bg-[#0a0a0a]/40 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden ${className}`}>
            {/* Header with Search */}
            <div className="p-3 border-b border-white/10 bg-gradient-to-r from-blue-600/10 to-transparent">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-600/20 flex items-center justify-center border border-blue-500/30">
                            <Globe className="w-4 h-4 text-blue-400 animate-pulse" />
                        </div>
                        <div>
                            <h2 className="text-sm font-bold text-white tracking-tight">
                                Service Network
                            </h2>
                            <p className="text-blue-100/30 text-[8px] uppercase tracking-[0.2em] font-black">
                                {totalCities} Jurisdictions
                            </p>
                        </div>
                    </div>

                    <div className="relative group max-w-xs w-full">
                        <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                            <Search className="h-3 w-3 text-white/20 group-focus-within:text-blue-400 transition-colors" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search cities..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-8 pr-2.5 py-1.5 bg-white/5 border border-white/10 rounded-lg text-[11px] text-white placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-blue-500/40 focus:border-transparent transition-all"
                        />
                        {searchTerm && (
                            <button 
                                onClick={() => setSearchTerm("")}
                                className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-white/20 hover:text-white"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Top Navigation for Counties */}
            {!searchTerm && (
                <div className="flex items-center gap-1 p-1 bg-white/[0.02] border-b border-white/5 overflow-x-auto no-scrollbar scroll-smooth">
                    {visibleGroups.map((group) => (
                        <button
                            key={group.county}
                            onClick={() => setActiveCounty(group.county)}
                            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all duration-200 ${
                                activeCounty === group.county
                                    ? "bg-blue-600 text-white shadow-sm shadow-blue-900/40"
                                    : "text-white/40 hover:bg-white/5 hover:text-white"
                            }`}
                        >
                            <div className={`w-1.5 h-1.5 rounded-full ${group.color} ${activeCounty === group.county ? "shadow-[0_0_4px_white]" : ""}`} />
                            <span className="text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">{group.county}</span>
                            <span className={`text-[8px] font-black ml-1 ${activeCounty === group.county ? "text-blue-200" : "text-white/10"}`}>
                                {group.cities.length}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            <div className="relative">
                <AnimatePresence mode="wait">
                    {searchTerm ? (
                        /* Search Results View */
                        <motion.div
                            key="search-results"
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -5 }}
                            className="p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-[300px] overflow-y-auto"
                        >
                            {filteredCities.length > 0 ? (
                                filteredCities.map(({ city, county, color }) => (
                                    <button
                                        key={`${county}-${city}`}
                                        onClick={() => onSelectCity?.(city, county)}
                                        className="flex flex-col p-2 rounded-lg bg-white/5 border border-white/5 hover:border-blue-500/50 hover:bg-blue-500/10 transition-all group/item text-left"
                                    >
                                        <div className="text-[11px] font-bold text-white group-hover/item:text-blue-400 transition-colors truncate">{city}</div>
                                        <div className="text-[8px] text-white/30 uppercase font-black truncate">{county}</div>
                                    </button>
                                ))
                            ) : (
                                <div className="col-span-full py-8 flex flex-col items-center justify-center text-white/10">
                                    <MapPin className="w-8 h-8 mb-2" />
                                    <p className="text-[10px] font-medium tracking-tight">No results for "{searchTerm}"</p>
                                </div>
                            )}
                        </motion.div>
                    ) : (
                        /* Default Browser View */
                        <motion.div
                            key="explorer"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="p-3 bg-white/[0.01]"
                        >
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 max-h-[300px] overflow-y-auto pr-1 thin-scrollbar">
                                {activeGroup?.cities.map((city) => (
                                    <motion.button
                                        key={city}
                                        whileHover={{ scale: 1.02, backgroundColor: "rgba(255,255,255,0.06)" }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => onSelectCity?.(city, activeCounty)}
                                        className="px-2.5 py-2 rounded-lg bg-white/5 border border-white/5 text-left transition-all hover:border-white/10 group/city"
                                    >
                                        <span className="text-[10px] font-medium text-white/60 group-hover/city:text-white transition-colors truncate block">{city}</span>
                                    </motion.button>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Footer Tip */}
            <div className="px-3 py-1.5 bg-white/[0.02] border-t border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-2.5 h-2.5 text-emerald-500/50" />
                    <span className="text-[8px] font-bold text-white/10 uppercase tracking-widest">Verified GIS Network</span>
                </div>
                <span className="text-[8px] font-bold text-white/10 uppercase">v2.1.0</span>
            </div>
        </div>
    );
};

export default JurisdictionSelector;
